import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Progress } from '@/shared/components/ui/progress';
import MobileScanInput from '../components/MobileScanInput';
import {
  Box,
  Package,
  CheckCircle2,
  ChevronRight,
  Printer,
  RotateCcw,
  Plus,
  Tag
} from 'lucide-react';
import { toast } from 'sonner';
import { useMobile } from '../context/MobileContext';
import { wmsEvents } from '@/shared/services/trailService';
import { packingAPI } from '@/shared/services/api';

const STEPS = {
  SELECT_ORDER: 'select_order',
  SCAN_ORDER: 'scan_order',
  PACK_ITEMS: 'pack_items',
  PRINT_LABEL: 'print_label',
  COMPLETE: 'complete'
};

const MobilePacking = () => {
  const navigate = useNavigate();
  const { setCurrentTask } = useMobile();
  const [currentStep, setCurrentStep] = useState(STEPS.SELECT_ORDER);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [packedItems, setPackedItems] = useState([]);
  const [cartons, setCartons] = useState([{ id: 'CTN-001', items: [] }]);
  const [currentCartonIndex, setCurrentCartonIndex] = useState(0);

  // Dynamic state for orders
  const [orders, setOrders] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);

  useEffect(() => {
    setCurrentTask({
      name: 'Packing',
      href: '/mobile/packing',
      description: 'Pack items into cartons'
    });
    fetchPackingQueue();
    return () => setCurrentTask(null);
  }, [setCurrentTask]);

  const fetchPackingQueue = async () => {
    setIsLoadingOrders(true);
    try {
      const data = await packingAPI.getQueue();
      setOrders(Array.isArray(data) ? data : data.queue || []);
    } catch (err) {
      toast.error('Failed to fetch packing queue');
    } finally {
      setIsLoadingOrders(false);
    }
  };

  const currentCarton = cartons[currentCartonIndex];
  const totalItems = items.length;
  const totalPacked = items.filter(i => i.packed).length;
  const progress = totalItems > 0 ? (totalPacked / totalItems) * 100 : 0;

  const handleSelectOrder = (order) => {
    setSelectedOrder(order);
    setCurrentStep(STEPS.SCAN_ORDER);
  };

  const handleOrderScanSuccess = async () => {
    const userId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'operator';

    try {
      const data = await packingAPI.startPacking(selectedOrder.id);

      // Set items and initial carton from backend
      setItems(data.items || []);
      if (data.active_carton) {
        setCartons([data.active_carton]);
      } else {
        setCartons([{ id: `CTN-${selectedOrder.id}-001`, items: [] }]);
      }

      // Emit trail events for packing started
      wmsEvents.packingSessionStarted(selectedOrder.id, userId);
      wmsEvents.packingOrderScanned(selectedOrder.id, userId);

      setCurrentStep(STEPS.PACK_ITEMS);
      toast.success('Order Ready', { description: `${data.items?.length || 0} items to pack` });
    } catch (err) {
      toast.error(err.message || 'Failed to start packing');
    }
  };

  const handleItemScanSuccess = async (barcode) => {
    const userId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'operator';

    const itemIndex = items.findIndex(item =>
      item.sku === barcode || barcode.includes(item.sku)
    );

    if (itemIndex >= 0 && !items[itemIndex].packed) {
      try {
        await packingAPI.verifyItem({
          order_id: selectedOrder.id,
          sku: items[itemIndex].sku,
          carton_id: currentCarton.id,
          quantity: 1
        });

        const packedItem = items[itemIndex];

        // Mark item as packed locally
        setItems(prev => prev.map((item, idx) =>
          idx === itemIndex ? { ...item, packed: true } : item
        ));

        // Add to current carton locally
        setCartons(prev => prev.map((carton, idx) =>
          idx === currentCartonIndex
            ? { ...carton, items: [...carton.items, items[itemIndex]] }
            : carton
        ));

        // Emit trail event for item packed
        wmsEvents.packingItemScanned(selectedOrder.id, currentCarton.id, {
          sku: packedItem.sku,
          description: packedItem.description
        }, userId);

        const newPackedCount = totalPacked + 1;

        if (newPackedCount === totalItems) {
          toast.success('All Items Packed', { description: 'Ready to print label' });
          setCurrentStep(STEPS.PRINT_LABEL);
        } else {
          toast.success('Item Packed', { description: `${totalItems - newPackedCount} remaining` });
        }
      } catch (err) {
        toast.error(err.message || 'Failed to verify item');
      }
    } else if (items[itemIndex]?.packed) {
      toast.warning('Already Packed', { description: 'This item is already in a carton' });
    } else {
      toast.error('Item Not Found', { description: 'Item not in this order' });
    }
  };

  const handleNewCarton = async () => {
    try {
      const data = await packingAPI.createCarton(selectedOrder.id);
      const newCarton = {
        id: data.carton_id || `CTN-${selectedOrder.id}-${String(cartons.length + 1).padStart(3, '0')}`,
        items: []
      };
      setCartons(prev => [...prev, newCarton]);
      setCurrentCartonIndex(cartons.length);
      toast.info('New Carton', { description: `Started ${newCarton.id}` });
    } catch (err) {
      toast.error(err.message || 'Failed to create new carton');
    }
  };

  const handleSealCarton = async () => {
    const userId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'operator';

    if (currentCarton.items.length === 0) {
      toast.error('Empty Carton', { description: 'Add items before sealing' });
      return;
    }

    try {
      await packingAPI.sealCarton({
        carton_id: currentCarton.id,
        weight: 1.5 // Mock weight capture
      });

      // Emit trail event for carton sealed
      wmsEvents.packingCartonSealed(selectedOrder.id, currentCarton.id, userId);

      toast.success('Carton Sealed', { description: currentCarton.id });

      if (totalPacked < totalItems) {
        handleNewCarton();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to seal carton');
    }
  };

  const handlePrintLabel = async () => {
    const userId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'operator';

    try {
      // Print labels for all cartons
      for (const carton of cartons) {
        await packingAPI.generateLabel(carton.id);
      }

      await packingAPI.completePacking(selectedOrder.id);

      // Emit trail event for packing completed
      wmsEvents.packingCompleted(selectedOrder.id, cartons.length, userId);

      toast.success('Labels Printed', { description: `Shipping labels for ${selectedOrder.id}` });
      setCurrentStep(STEPS.COMPLETE);
      setOrders(prev => prev.filter(o => o.id !== selectedOrder.id));
    } catch (err) {
      toast.error(err.message || 'Failed to complete packing');
    }
  };

  const startNewOrder = () => {
    setCurrentStep(STEPS.SELECT_ORDER);
    setSelectedOrder(null);
    setItems([]);
    setPackedItems([]);
    setCartons([{ id: 'CTN-001', items: [] }]);
    setCurrentCartonIndex(0);
  };

  const renderStep = () => {
    switch (currentStep) {
      case STEPS.SELECT_ORDER:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Orders to Pack</h2>
              <Badge>{orders.length} orders</Badge>
            </div>

            {orders.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
                  <h3 className="font-semibold text-lg">All Orders Packed!</h3>
                  <p className="text-muted-foreground">No pending pack orders</p>
                </CardContent>
              </Card>
            ) : (
              orders.map((order) => (
                <Card
                  key={order.id}
                  className="cursor-pointer hover:shadow-md active:scale-[0.98] transition-all"
                  onClick={() => handleSelectOrder(order)}
                  data-testid={`order-${order.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-xl bg-purple-500 text-white">
                        <Box className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{order.id}</h3>
                          <Badge variant="secondary" className="text-green-600">Picked</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{order.customer}</p>
                        <Badge variant="outline" className="mt-1">{order.items} items</Badge>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        );

      case STEPS.SCAN_ORDER:
        return (
          <div className="space-y-4">
            <Card className="bg-purple-50 dark:bg-purple-950/20 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Box className="h-5 w-5 text-purple-600" />
                  <div>
                    <p className="font-semibold">{selectedOrder.id}</p>
                    <p className="text-sm text-muted-foreground">{selectedOrder.customer}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <MobileScanInput
              title="Scan Order/Tote"
              subtitle="Scan the order barcode or tote to start packing"
              gateId="GATE_PACK_ORDER"
              barcodeType="ORDER"
              scanContext="PACKING"
              expectedValue={selectedOrder.id}
              onScanSuccess={handleOrderScanSuccess}
              validateScan={async (barcode) => ({
                success: barcode === selectedOrder.id || barcode.includes(selectedOrder.id),
                result: 'OK'
              })}
            />

            <Button variant="outline" onClick={startNewOrder} className="w-full">
              Cancel
            </Button>
          </div>
        );

      case STEPS.PACK_ITEMS:
        return (
          <div className="space-y-4">
            {/* Progress Header */}
            <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm opacity-80">{selectedOrder.id}</p>
                    <p className="text-2xl font-bold">{totalPacked} / {totalItems}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm opacity-80">Carton</p>
                    <p className="text-xl font-bold">{currentCartonIndex + 1}/{cartons.length}</p>
                  </div>
                </div>
                <Progress value={progress} className="h-2 bg-white/20" />
              </CardContent>
            </Card>

            {/* Current Carton */}
            <Card className="border-2 border-purple-300">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Box className="h-4 w-4" />
                    {currentCarton.id}
                  </span>
                  <Badge>{currentCarton.items.length} items</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 max-h-32 overflow-y-auto">
                {currentCarton.items.length === 0 ? (
                  <p className="p-4 text-center text-sm text-muted-foreground">
                    Scan items to add to carton
                  </p>
                ) : (
                  currentCarton.items.map((item, idx) => (
                    <div key={idx} className="px-4 py-2 border-b last:border-0 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <span className="text-sm">{item.sku}</span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Scanner */}
            <MobileScanInput
              title="Scan Item into Carton"
              subtitle={`Packing into ${currentCarton.id}`}
              gateId="GATE_PACK_ITEM"
              barcodeType="SKU"
              scanContext="PACKING"
              onScanSuccess={handleItemScanSuccess}
              chainMapping={{ carton_id: currentCarton.id }}
              validateScan={async (barcode) => {
                const item = items.find(i => i.sku === barcode || barcode.includes(i.sku));
                if (!item) return { success: false, result: 'REJECTED', message: 'Item not in order' };
                if (item.packed) return { success: false, result: 'REJECTED', message: 'Already packed' };
                return { success: true, result: 'OK' };
              }}
            />

            {/* Unpacked Items */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Remaining Items</CardTitle>
              </CardHeader>
              <CardContent className="p-0 max-h-32 overflow-y-auto">
                {items.filter(i => !i.packed).map((item) => (
                  <div key={item.id} className="px-4 py-2 border-b last:border-0 flex items-center gap-2">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{item.sku} - {item.description}</span>
                  </div>
                ))}
                {items.every(i => i.packed) && (
                  <p className="p-4 text-center text-sm text-green-600">All items packed!</p>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleSealCarton} className="flex-1">
                Seal Carton
              </Button>
              <Button variant="outline" onClick={handleNewCarton} className="flex-1 gap-2">
                <Plus className="h-4 w-4" />
                New Carton
              </Button>
            </div>

            {totalPacked === totalItems && (
              <Button onClick={() => setCurrentStep(STEPS.PRINT_LABEL)} className="w-full gap-2">
                <Tag className="h-4 w-4" />
                Print Shipping Label
              </Button>
            )}
          </div>
        );

      case STEPS.PRINT_LABEL:
        return (
          <div className="space-y-6 py-4">
            <Card className="border-2 border-purple-300">
              <CardContent className="p-6 text-center">
                <Printer className="h-12 w-12 mx-auto text-purple-600 mb-4" />
                <h2 className="text-xl font-bold mb-2">Ready to Print Label</h2>
                <p className="text-muted-foreground mb-4">
                  {cartons.length} carton(s) for {selectedOrder.id}
                </p>

                <div className="space-y-2 text-left bg-muted/50 rounded-lg p-4 mb-4">
                  <p className="text-sm"><strong>Order:</strong> {selectedOrder.id}</p>
                  <p className="text-sm"><strong>Customer:</strong> {selectedOrder.customer}</p>
                  <p className="text-sm"><strong>Items:</strong> {totalItems}</p>
                  <p className="text-sm"><strong>Cartons:</strong> {cartons.length}</p>
                </div>
              </CardContent>
            </Card>

            <Button onClick={handlePrintLabel} className="w-full gap-2" size="lg">
              <Printer className="h-5 w-5" />
              Print Label & Complete
            </Button>

            <Button variant="outline" onClick={() => setCurrentStep(STEPS.PACK_ITEMS)} className="w-full">
              Back to Packing
            </Button>
          </div>
        );

      case STEPS.COMPLETE:
        return (
          <div className="space-y-6 text-center py-8">
            <div className="mx-auto w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Packing Complete!</h2>
              <p className="text-muted-foreground mt-2">{selectedOrder.id} ready for dispatch</p>
            </div>
            <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto">
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-2xl font-bold">{totalItems}</p>
                <p className="text-xs text-muted-foreground">Items</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-2xl font-bold">{cartons.length}</p>
                <p className="text-xs text-muted-foreground">Cartons</p>
              </div>
            </div>
            <div className="flex gap-3 max-w-sm mx-auto">
              <Button variant="outline" onClick={startNewOrder} className="flex-1">
                <RotateCcw className="h-4 w-4 mr-2" />
                Next Order
              </Button>
              <Button onClick={() => navigate('/mobile/loadout')} className="flex-1">
                Go to Load-out
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-4" data-testid="mobile-packing">
      {renderStep()}
    </div>
  );
};

export default MobilePacking;
